import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher, customFetcherRaw } from './customFetcher';
import {
    fileItemSchema,
    folderFilesSchema,
    IStorageGroup,
    storageGroupFallbacks,
    storageNamedGroupsSchema,
    folderTreeNodeSchema,
    folderUpsertSchema,
    storageGroupsSchema,
    folderTreeSchema,
    IFileItem,
    IFolderTreeNode,
    IFolderUpsertInput,
} from '../Interfaces/IStorage';

export const storageTreeQueryKey = ['storage', 'tree'] as const;
export const storageFolderFilesQueryKey = (folderId: number) => ['storage', 'folders', folderId, 'files'] as const;
export const storageGroupsQueryKey = ['storage', 'groups'] as const;

export const useStorageTreeQuery = () =>
    useQuery<IFolderTreeNode[], Error>({
        queryKey: storageTreeQueryKey,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const data = await customFetcher<unknown>({ endpoint: '/storage/folders/tree' });
            return folderTreeSchema.parse(data);
        },
        initialData: [],
    });

export const useFolderFilesQuery = (folderId: number | null) =>
    useQuery<IFileItem[], Error>({
        queryKey: folderId ? storageFolderFilesQueryKey(folderId) : ['storage', 'folders', 'none', 'files'],
        refetchOnWindowFocus: false,
        enabled: folderId !== null,
        queryFn: async () => {
            if (folderId === null) {
                return [];
            }
            const data = await customFetcher<unknown>({ endpoint: `/storage/folders/${folderId}/files` });
            return folderFilesSchema.parse(data);
        },
        initialData: [],
    });

export const useStorageGroupsQuery = () =>
    useQuery<IStorageGroup[], Error>({
        queryKey: storageGroupsQueryKey,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            try {
                const data = await customFetcher<unknown>({ endpoint: '/accounts/custom-groups/' });
                const namedGroups = storageNamedGroupsSchema.parse(data);
                return namedGroups.map((group) => ({ id: group.idCustomGroup, name: group.name }));
            } catch (error) {
                if (error instanceof Error && error.message.includes('404')) {
                    try {
                        const fallbackApiData = await customFetcher<unknown>({ endpoint: '/groups/' });
                        return storageGroupsSchema.parse(fallbackApiData);
                    } catch (groupsError) {
                        if (groupsError instanceof Error && groupsError.message.includes('404')) {
                            return storageGroupFallbacks;
                        }
                        throw groupsError;
                    }
                }
                throw error;
            }
        },
        initialData: [],
    });

export const useCreateFolderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<IFolderTreeNode, Error, IFolderUpsertInput>({
        mutationFn: async (payload) => {
            const data = await customFetcher<unknown>({
                endpoint: '/storage/folders/',
                method: 'POST',
                body: payload,
            });
            return folderTreeNodeSchema.parse(data);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['storage'] });
        },
    });
};

export const useUpdateFolderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<IFolderTreeNode, Error, { folderId: number; payload: IFolderUpsertInput }>({
        mutationFn: async ({ folderId, payload }) => {
            const data = await customFetcher<unknown>({
                endpoint: `/storage/folders/${folderId}/`,
                method: 'PUT',
                body: payload,
            });
            return folderTreeNodeSchema.parse(data);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['storage'] });
        },
    });
};

export const useDeleteFolderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { folderId: number }>({
        mutationFn: async ({ folderId }) => {
            await customFetcher<void>({
                endpoint: `/storage/folders/${folderId}/`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['storage'] });
        },
    });
};

export const useUploadFileMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<IFileItem, Error, { folderId: number; file: File }>({
        mutationFn: async ({ folderId, file }) => {
            const formData = new FormData();
            formData.append('file', file);
            const data = await customFetcher<unknown>({
                endpoint: `/storage/folders/${folderId}/files`,
                method: 'POST',
                body: formData,
            });
            return fileItemSchema.parse(data);
        },
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({ queryKey: storageTreeQueryKey });
            void queryClient.invalidateQueries({ queryKey: storageFolderFilesQueryKey(variables.folderId) });
        },
    });
};

export const useDeleteFileMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { folderId: number; fileId: number }>({
        mutationFn: async ({ fileId }) => {
            await customFetcher<void>({
                endpoint: `/storage/files/${fileId}/`,
                method: 'DELETE',
            });
        },
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({ queryKey: storageTreeQueryKey });
            void queryClient.invalidateQueries({ queryKey: storageFolderFilesQueryKey(variables.folderId) });
        },
    });
};

export const useToggleFolderFavoriteMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<{ folder_id: number; is_favorite: boolean }, Error, { folderId: number; isFavorite: boolean }>({
        mutationFn: async ({ folderId, isFavorite }) => customFetcher({
            endpoint: `/storage/folders/${folderId}/favorite`,
            method: isFavorite ? 'PUT' : 'DELETE',
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['storage'] });
        },
    });
};

export const useToggleFileFavoriteMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<{ file_id: number; is_favorite: boolean }, Error, { folderId: number; fileId: number; isFavorite: boolean }>({
        mutationFn: async ({ fileId, isFavorite }) => customFetcher({
            endpoint: `/storage/files/${fileId}/favorite`,
            method: isFavorite ? 'PUT' : 'DELETE',
        }),
        onSuccess: (_result, variables) => {
            void queryClient.invalidateQueries({ queryKey: storageTreeQueryKey });
            void queryClient.invalidateQueries({ queryKey: storageFolderFilesQueryKey(variables.folderId) });
        },
    });
};

export const useRenameFileMutation = () => {
    const queryClient = useQueryClient();
    return useMutation<IFileItem, Error, { folderId: number; fileId: number; original_name: string }>({
        mutationFn: async ({ fileId, original_name }) => {
            const data = await customFetcher<unknown>({
                endpoint: `/storage/files/${fileId}/`,
                method: 'PUT',
                body: { original_name },
            });
            return fileItemSchema.parse(data);
        },
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({ queryKey: storageTreeQueryKey });
            void queryClient.invalidateQueries({ queryKey: storageFolderFilesQueryKey(variables.folderId) });
        },
    });
};

export const parseFolderUpsertInput = (value: unknown): IFolderUpsertInput => folderUpsertSchema.parse(value);

const getFilenameFromContentDisposition = (contentDisposition: string | null, fallback: string): string => {
    if (!contentDisposition) {
        return fallback;
    }

    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) {
        return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    if (asciiMatch?.[1]) {
        return asciiMatch[1];
    }

    return fallback;
};

export const downloadStorageFile = async (
    fileId: number,
    fallbackFileName: string
): Promise<{ blob: Blob; fileName: string }> => {
    const response = await customFetcherRaw({
        endpoint: `/storage/files/${fileId}/download`,
        method: 'GET',
    });
    const fileName = getFilenameFromContentDisposition(
        response.headers.get('content-disposition'),
        fallbackFileName
    );
    const blob = await response.blob();
    return { blob, fileName };
};
